import _slicedToArray from "/vercel/path0/node_modules/.pnpm/@babel+runtime@7.15.4/node_modules/@babel/runtime/helpers/esm/slicedToArray";
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import styled from 'styled-components';
import React, { forwardRef, useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { CommentExtension } from '../extensions/comment';
import { styleVariables } from '../../../style';
import TextStyle from '@tiptap/extension-text-style';
import { CustomTextStyle } from '../extensions/custom-text-style';
import { CommentPreviewContent, CommentPreviewSurface } from './comment-preview-surface.component';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
var DEFAULT_COMMENT_COLOR = '#fcd46c';
var POPUP_VIEWPORT_PADDING = 8;
var POPUP_OFFSET = 8;
var COLLAPSED_COMMENT_HEIGHT = 105;
var StyledLayout = styled.div.withConfig({
  displayName: "editor-comment-view-onlycomponent__StyledLayout",
  componentId: "sc-1ibl2x0-0"
})([".tiptap{margin-bottom:", ";}.my-comment{cursor:pointer;text-decoration-line:underline;text-decoration-thickness:2px;text-decoration-color:rgba(15,23,42,0.35);text-underline-offset:4px;transition:box-shadow 0.2s ease;&:hover,&:focus-visible{box-shadow:inset 0 0 0 9999px rgba(15,23,42,0.08);}}custom-class{background-color:", ";}.ProseMirror{padding:", ";border:", " solid ", ";border-top:none;border-bottom-left-radius:", ";border-bottom-right-radius:", ";outline:none !important;img{max-width:100%;}}p.is-editor-empty:first-child::before{color:", ";font-style:italic;content:attr(data-placeholder);float:left;height:0;pointer-events:none;}"], styleVariables.margin.large, function (props) {
  return props.theme.color.primary;
}, styleVariables.padding.large, styleVariables.borderWidth.small, function (props) {
  return props.theme.color.borderPrimary;
}, styleVariables.borderRadius.medium, styleVariables.borderRadius.medium, function (props) {
  return props.theme.color.borderDefault;
});
var StyledEditorContent = styled(EditorContent).withConfig({
  displayName: "editor-comment-view-onlycomponent__StyledEditorContent",
  componentId: "sc-1ibl2x0-1"
})(["max-height:", ";overflow-y:", ";& > *{pointer-events:none;}.my-comment{pointer-events:auto;}"], function (props) {
  return props.scrollable ? '70vh' : 'none';
}, function (props) {
  return props.scrollable ? 'auto' : 'visible';
});
var StyledViewOnlyPopup = styled(CommentPreviewSurface).withConfig({
  displayName: "editor-comment-view-onlycomponent__StyledViewOnlyPopup",
  componentId: "sc-1ibl2x0-2"
})(["position:fixed;z-index:1000;width:min(400px,calc(100vw - 16px));overflow:hidden;font-family:inherit;"]);
var StyledPopupHeader = styled.div.withConfig({
  displayName: "editor-comment-view-onlycomponent__StyledPopupHeader",
  componentId: "sc-1ibl2x0-3"
})(["display:flex;align-items:center;gap:", ";min-height:52px;padding:", " ", ";background:", ";color:", ";"], styleVariables.gap.medium, styleVariables.padding.medium, styleVariables.padding.large, function (props) {
  return props.$commentColor;
}, function (props) {
  return props.$headerTextColor;
});
var StyledTeacherAvatar = styled.span.withConfig({
  displayName: "editor-comment-view-onlycomponent__StyledTeacherAvatar",
  componentId: "sc-1ibl2x0-4"
})(["display:inline-flex;align-items:center;justify-content:center;width:32px;height:32px;border-radius:50%;background:rgba(255,255,255,0.72);color:", ";font-size:", ";font-weight:", ";flex-shrink:0;"], function (props) {
  return props.$headerTextColor;
}, styleVariables.font.fontSizeSmall, styleVariables.font.fontWeightStrong);
var StyledTeacherName = styled.span.withConfig({
  displayName: "editor-comment-view-onlycomponent__StyledTeacherName",
  componentId: "sc-1ibl2x0-5"
})(["font-size:", ";font-weight:", ";"], styleVariables.font.fontSizeSmall, styleVariables.font.fontWeightStrong);
var StyledPopupBody = styled.div.withConfig({
  displayName: "editor-comment-view-onlycomponent__StyledPopupBody",
  componentId: "sc-1ibl2x0-6"
})(["padding:", ";"], styleVariables.padding.large);
var StyledCommentContent = styled(CommentPreviewContent).withConfig({
  displayName: "editor-comment-view-onlycomponent__StyledCommentContent",
  componentId: "sc-1ibl2x0-7"
})(["overflow:hidden;max-height:", ";white-space:pre-wrap;word-break:break-word;transition:max-height 220ms ease-in-out;@media (prefers-reduced-motion:reduce){transition:none;}"], function (_ref) {
  var $isExpanded = _ref.$isExpanded,
      $expandedHeight = _ref.$expandedHeight;
  return "".concat($isExpanded ? $expandedHeight : COLLAPSED_COMMENT_HEIGHT, "px");
});
var StyledCommentToggle = styled.button.withConfig({
  displayName: "editor-comment-view-onlycomponent__StyledCommentToggle",
  componentId: "sc-1ibl2x0-8"
})(["margin-top:", ";padding:0;border:0;background:transparent;color:", ";cursor:pointer;font:inherit;font-size:", ";font-weight:", ";text-decoration:underline;"], styleVariables.margin.medium, function (props) {
  return props.$commentColor;
}, styleVariables.font.fontSizeSupport, styleVariables.font.fontWeightStrong);

var isCssColor = function isCssColor(color) {
  return Boolean(color) && typeof CSS !== 'undefined' && CSS.supports('color', color);
};

var getCommentColor = function getCommentColor(element) {
  var colorCandidates = [element.dataset.commentColor, element.style.backgroundColor, window.getComputedStyle(element).backgroundColor];
  return colorCandidates.find(function (color) {
    return isCssColor(color) && color !== 'transparent' && (color === null || color === void 0 ? void 0 : color.replace(/\s/g, '')) !== 'rgba(0,0,0,0)';
  }) || DEFAULT_COMMENT_COLOR;
};

var getRgb = function getRgb(color) {
  var hex = color.replace('#', '').trim();

  if (/^[0-9a-f]{3}$/i.test(hex)) {
    return hex.split('').map(function (value) {
      return Number.parseInt("".concat(value).concat(value), 16);
    });
  }

  if (/^[0-9a-f]{6}$/i.test(hex)) {
    return [Number.parseInt(hex.slice(0, 2), 16), Number.parseInt(hex.slice(2, 4), 16), Number.parseInt(hex.slice(4, 6), 16)];
  }

  var rgb = color.match(/^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : null;
};

var getHeaderTextColor = function getHeaderTextColor(color) {
  var rgb = getRgb(color);

  if (!rgb) {
    return styleVariables.colors.grey100;
  }

  var _rgb = _slicedToArray(rgb, 3),
      red = _rgb[0],
      green = _rgb[1],
      blue = _rgb[2];

  var luminance = (red * 299 + green * 587 + blue * 114) / 1000;
  return luminance < 150 ? styleVariables.colors.gray1 : styleVariables.colors.grey100;
};

var getTeacherInitial = function getTeacherInitial(teacherName) {
  return teacherName.trim().charAt(0).toUpperCase();
};

export var EditorCommentViewOnly = /*#__PURE__*/forwardRef(function (_ref2, ref) {
  var content = _ref2.content,
      _ref2$scrollable = _ref2.scrollable,
      scrollable = _ref2$scrollable === void 0 ? false : _ref2$scrollable,
      comments = _ref2.comments,
      labels = _ref2.labels;
  var layoutRef = useRef(null);
  var popupRef = useRef(null);
  var commentContentRef = useRef(null);

  var _useState = useState(null),
      activeComment = _useState[0],
      setActiveComment = _useState[1];

  var _useState2 = useState(null),
      popupPosition = _useState2[0],
      setPopupPosition = _useState2[1];

  var _useState3 = useState(false),
      isCommentExpanded = _useState3[0],
      setIsCommentExpanded = _useState3[1];

  var _useState4 = useState(COLLAPSED_COMMENT_HEIGHT),
      commentContentHeight = _useState4[0],
      setCommentContentHeight = _useState4[1];

  var _useState5 = useState(false),
      hasCommentOverflow = _useState5[0],
      setHasCommentOverflow = _useState5[1];

  var setLayoutRef = useCallback(function (node) {
    layoutRef.current = node;

    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      ref.current = node;
    }
  }, [ref]);
  var hideComment = useCallback(function () {
    setActiveComment(null);
    setPopupPosition(null);
  }, []);
  var showComment = useCallback(function (element) {
    var commentId = element.dataset.commentId;
    var comment = comments.find(function (_ref3) {
      var id = _ref3.id;
      return id === commentId;
    });

    if (!comment || !commentId) {
      return;
    }

    var _element$getBoundingC = element.getBoundingClientRect(),
        top = _element$getBoundingC.top,
        bottom = _element$getBoundingC.bottom,
        left = _element$getBoundingC.left;

    var commentColor = getCommentColor(element);
    setIsCommentExpanded(false);
    setActiveComment({
      id: commentId,
      content: comment.content || '',
      color: commentColor,
      anchor: {
        top: top,
        bottom: bottom,
        left: left
      }
    });
  }, [comments]);
  var updatePopupPosition = useCallback(function () {
    if (!activeComment || !popupRef.current) {
      return;
    }

    var popupRect = popupRef.current.getBoundingClientRect();
    var maximumLeft = Math.max(POPUP_VIEWPORT_PADDING, window.innerWidth - popupRect.width - POPUP_VIEWPORT_PADDING);
    var left = Math.min(Math.max(activeComment.anchor.left, POPUP_VIEWPORT_PADDING), maximumLeft);
    var preferredTop = activeComment.anchor.top - popupRect.height - POPUP_OFFSET;
    var fallbackTop = activeComment.anchor.bottom + POPUP_OFFSET;
    var maximumTop = Math.max(POPUP_VIEWPORT_PADDING, window.innerHeight - popupRect.height - POPUP_VIEWPORT_PADDING);
    var top = preferredTop >= POPUP_VIEWPORT_PADDING ? preferredTop : Math.min(Math.max(fallbackTop, POPUP_VIEWPORT_PADDING), maximumTop);
    setPopupPosition({
      top: top,
      left: left
    });
  }, [activeComment]);
  var editor = useEditor({
    extensions: [StarterKit, Underline, TextStyle, CustomTextStyle, CommentExtension.configure({
      HTMLAttributes: {
        "class": 'my-comment'
      },
      onCommentActivated: function onCommentActivated() {}
    })],
    content: content || '',
    editable: false
  });
  useEffect(function () {
    if (!editor) return;
    return function () {
      return editor.destroy();
    };
  }, [editor]);
  useEffect(function () {
    if (!editor) return;
    var listeners = [];
    var timer = window.setTimeout(function () {
      var _layoutRef$current;

      var commentElements = (_layoutRef$current = layoutRef.current) === null || _layoutRef$current === void 0 ? void 0 : _layoutRef$current.querySelectorAll('[data-comment-id]');
      commentElements === null || commentElements === void 0 ? void 0 : commentElements.forEach(function (element) {
        var handleClick = function handleClick() {
          return showComment(element);
        };

        var handleKeyDown = function handleKeyDown(event) {
          if (event.key !== 'Enter' && event.key !== ' ') return;
          event.preventDefault();
          showComment(element);
        };

        element.tabIndex = 0;
        element.setAttribute('aria-label', labels.comment);
        element.setAttribute('aria-describedby', "editor-comment-tooltip-".concat(element.dataset.commentId));
        element.addEventListener('click', handleClick);
        element.addEventListener('keydown', handleKeyDown);
        listeners.push({
          element: element,
          handleClick: handleClick,
          handleKeyDown: handleKeyDown
        });
      });
    }, 0);
    return function () {
      clearTimeout(timer);
      listeners.forEach(function (_ref4) {
        var element = _ref4.element,
            handleClick = _ref4.handleClick,
            handleKeyDown = _ref4.handleKeyDown;
        element.removeEventListener('click', handleClick);
        element.removeEventListener('keydown', handleKeyDown);
      });
    };
  }, [comments, content, editor, labels.comment, showComment]);
  useEffect(function () {
    if (!activeComment) return;

    var handleOutsideClick = function handleOutsideClick(event) {
      var _popupRef$current;

      var target = event.target;
      if (!(target instanceof Node) || (_popupRef$current = popupRef.current) !== null && _popupRef$current !== void 0 && _popupRef$current.contains(target)) return;
      var highlightedComment = target instanceof Element ? target.closest('[data-comment-id]') : null;
      if ((highlightedComment === null || highlightedComment === void 0 ? void 0 : highlightedComment.getAttribute('data-comment-id')) === activeComment.id) return;
      hideComment();
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return function () {
      return document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [activeComment, hideComment]);
  useLayoutEffect(function () {
    if (!activeComment || !commentContentRef.current) {
      return;
    }

    var contentHeight = commentContentRef.current.scrollHeight;
    setCommentContentHeight(contentHeight);
    setHasCommentOverflow(contentHeight > COLLAPSED_COMMENT_HEIGHT);
  }, [activeComment]);
  useLayoutEffect(function () {
    if (!activeComment) {
      return;
    }

    updatePopupPosition();
  }, [activeComment, commentContentHeight, isCommentExpanded, updatePopupPosition]);
  useEffect(function () {
    if (!activeComment) {
      return;
    }

    var handleViewportChange = function handleViewportChange() {
      return updatePopupPosition();
    };

    window.addEventListener('resize', handleViewportChange);
    window.addEventListener('scroll', handleViewportChange, true);
    return function () {
      window.removeEventListener('resize', handleViewportChange);
      window.removeEventListener('scroll', handleViewportChange, true);
    };
  }, [activeComment, updatePopupPosition]);
  var teacherName = labels.teacherName || 'Teacher';
  var headerTextColor = activeComment ? getHeaderTextColor(activeComment.color) : '';
  return /*#__PURE__*/_jsxs(StyledLayout, {
    ref: setLayoutRef,
    children: [/*#__PURE__*/_jsx(StyledEditorContent, {
      editor: editor,
      scrollable: scrollable
    }), activeComment && /*#__PURE__*/_jsxs(StyledViewOnlyPopup, {
      ref: popupRef,
      id: "editor-comment-tooltip-".concat(activeComment.id),
      role: "dialog",
      "aria-label": labels.comment,
      $commentColor: activeComment.color,
      $borderMode: "full",
      $headerTextColor: headerTextColor,
      style: {
        top: (popupPosition === null || popupPosition === void 0 ? void 0 : popupPosition.top) || 0,
        left: (popupPosition === null || popupPosition === void 0 ? void 0 : popupPosition.left) || 0,
        visibility: popupPosition ? 'visible' : 'hidden'
      },
      children: [/*#__PURE__*/_jsxs(StyledPopupHeader, {
        $commentColor: activeComment.color,
        $headerTextColor: headerTextColor,
        children: [/*#__PURE__*/_jsx(StyledTeacherAvatar, {
          $commentColor: activeComment.color,
          $headerTextColor: headerTextColor,
          children: getTeacherInitial(teacherName)
        }), /*#__PURE__*/_jsx(StyledTeacherName, {
          children: teacherName
        })]
      }), /*#__PURE__*/_jsxs(StyledPopupBody, {
        children: [/*#__PURE__*/_jsx(StyledCommentContent, {
          ref: commentContentRef,
          $isExpanded: isCommentExpanded,
          $expandedHeight: commentContentHeight,
          children: activeComment.content
        }), hasCommentOverflow && /*#__PURE__*/_jsx(StyledCommentToggle, {
          type: "button",
          $commentColor: activeComment.color,
          "aria-expanded": isCommentExpanded,
          onClick: function onClick() {
            return setIsCommentExpanded(function (previousValue) {
              return !previousValue;
            });
          },
          children: isCommentExpanded ? labels.seeLess || 'See Less' : labels.seeMore || 'See More'
        })]
      })]
    })]
  });
});
EditorCommentViewOnly.displayName = 'EditorCommentViewOnly';